import React from 'react';
import { useQuery } from '@apollo/client';
import { Link } from 'react-router-dom';
import { useAccount } from '../../context/AccountContext';
import { GET_TINYCMS_SEARCH_ANALYTICS } from '../../graphql';
import type { SearchAnalytics as SearchNumbers, WeekCount } from './todayData';
import { Days, Ghost } from './analyticsParts';
import { HomeTag, isHomePath } from './HomeTag';
import { RollNumber } from '../common/RollNumber';

/**
 * Content → Analytics → Search: clicks, impressions, position and
 * click-through rate from Google Search Console over the range, the queries
 * people typed and the pages they landed on, as the engine last synced them
 * (never a live call). Nothing until tinycms has verified the site with
 * Google (Manage › Site › Search); then grey shapes until the first sync.
 */

const SEARCH_SETTINGS = '/content?view=manage&area=site#search';

const when = (iso: string | null | undefined) =>
  iso ? new Date(`${iso.slice(0, 10)}T12:00:00Z`).toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) : '';

function change(c: WeekCount | undefined, days: number) {
  const vs = `vs previous ${days} days`;
  if (!c) return { lead: '', rest: vs, tone: 'up' };
  if (!c.previous) return { lead: '', rest: `None in the previous ${days} days`, tone: 'up' };
  const pct = Math.round(((c.count - c.previous) / c.previous) * 100);
  if (pct === 0) return { lead: 'Same', rest: ` as the previous ${days} days`, tone: 'up' };
  return { lead: `${pct > 0 ? '+' : '−'}${Math.abs(pct)}%`, rest: ` ${vs}`, tone: pct > 0 ? 'up' : 'down' };
}

const CountTile: React.FC<{ label: string; hint: string; value: WeekCount | undefined; days: number; ready: boolean }> = ({
  label,
  hint,
  value,
  days,
  ready,
}) => {
  const delta = change(value, days);
  return (
    <div className="an-tile" title={hint}>
      <span className="an-tile-label">{label}</span>
      <span className="an-tile-value">
        {ready && value ? <RollNumber value={value.count} /> : <Ghost width={56} height={26} />}
      </span>
      <span className="an-tile-sub">
        {delta.lead ? <span data-tone={delta.tone}>{delta.lead}</span> : null}
        {delta.rest}
      </span>
    </div>
  );
};

/** Position: lower is better, so a drop in the number shows as "up". */
const ValueTile: React.FC<{
  label: string;
  hint: string;
  value: { value: number | null; previous: number | null } | undefined;
  format: (v: number) => string;
  lowerIsBetter?: boolean;
  ready: boolean;
}> = ({ label, hint, value, format, lowerIsBetter, ready }) => {
  const v = value?.value ?? null;
  const p = value?.previous ?? null;
  let lead = '';
  let tone = 'up';
  if (v != null && p != null && p !== 0) {
    const diff = v - p;
    const better = lowerIsBetter ? diff < 0 : diff > 0;
    lead = diff === 0 ? 'Same' : `${diff > 0 ? '+' : '−'}${format(Math.abs(diff))}`;
    tone = diff === 0 || better ? 'up' : 'down';
  }
  return (
    <div className="an-tile" title={hint}>
      <span className="an-tile-label">{label}</span>
      <span className="an-tile-value">{ready && v != null ? <RollNumber value={format(v)} /> : ready ? '—' : <Ghost width={56} height={26} />}</span>
      <span className="an-tile-sub">
        {lead ? <span data-tone={tone}>{lead}</span> : null}
        {lead ? ' vs before' : 'vs before'}
      </span>
    </div>
  );
};

const GhostRows: React.FC = () => (
  <div className="an-rows" aria-hidden="true">
    {[88, 64, 52, 40, 31].map((w, i) => (
      <div key={i} className="an-row">
        <span className="an-rank">{i + 1}</span>
        <span className="an-row-main">
          <span className="ink-ghost" style={{ width: `${w}%`, height: 11 }} />
        </span>
        <span className="ink-ghost" style={{ width: 40, height: 11 }} />
      </div>
    ))}
  </div>
);

export const SearchSection: React.FC<{ days: Days }> = ({ days }) => {
  const { accountId } = useAccount();
  const { data, loading } = useQuery<{ tinycmsSearchAnalytics: SearchNumbers }>(GET_TINYCMS_SEARCH_ANALYTICS, {
    variables: { accountId, days },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  const s = data?.tinycmsSearchAnalytics;
  if (!s) return null;
  const ready = Boolean(s.ranges) && !loading;
  const queries = s.topQueries || [];
  const pages = s.topPages || [];

  return (
    <section id="search" aria-label="Search">
      <div className="an-top" style={{ marginTop: 28 }}>
        <div className="an-card-title">Search</div>
        <span className="an-source">
          {s.live && s.throughDay
            ? `From Google Search · through ${when(s.throughDay)}`
            : s.live
              ? 'From Google Search · first numbers on their way'
              : ''}
        </span>
      </div>

      {!s.live ? (
        <div className="an-note">
          Clicks, impressions and the searches that find you come from Google Search Console once
          tinycms has proven your site to Google. <Link to={SEARCH_SETTINGS}>Manage › Site › Search</Link>{' '}
          shows where that stands.
        </div>
      ) : null}
      {s.state === 'failed' && s.error ? <div className="an-note">{s.error}</div> : null}

      <div className="an-tiles">
        <CountTile label="Clicks" hint="Times someone clicked through from Google" value={s.ranges?.clicks} days={days} ready={ready} />
        <CountTile label="Impressions" hint="Times a page of yours showed in Google results" value={s.ranges?.impressions} days={days} ready={ready} />
        <ValueTile
          label="Average position"
          hint="Where your pages ranked, weighted by how often they showed"
          value={s.ranges?.position}
          format={(v) => v.toFixed(1)}
          lowerIsBetter
          ready={ready}
        />
        <ValueTile
          label="Click-through"
          hint="Clicks as a share of impressions"
          value={s.ranges?.ctr}
          format={(v) => `${(v * 100).toFixed(1)}%`}
          ready={ready}
        />
      </div>

      <div className="an-pair">
        <div className="an-card">
          <div className="an-card-head">
            <div className="an-card-title">What people searched for</div>
            <div className="an-card-sub">Clicks · impressions · position</div>
          </div>
          {ready && queries.length ? (
            <div className="an-rows">
              {queries.map((q) => (
                <div key={q.query} className="an-row">
                  <span className="an-rank">{q.rank}</span>
                  <span className="an-row-main">
                    <span className="an-row-title">{q.query}</span>
                  </span>
                  <span className="an-row-n">
                    {q.clicks.toLocaleString()} · {q.impressions.toLocaleString()}
                    {q.position != null ? ` · #${q.position.toFixed(1)}` : ''}
                  </span>
                </div>
              ))}
            </div>
          ) : ready ? (
            <div className="an-empty-line">Not enough searches yet for Google to show any.</div>
          ) : (
            <GhostRows />
          )}
        </div>
        <div className="an-card">
          <div className="an-card-head">
            <div className="an-card-title">Pages found through Google</div>
            <div className="an-card-sub">Clicks · impressions · position</div>
          </div>
          {ready && pages.length ? (
            <div className="an-rows">
              {pages.map((p) => (
                <div key={p.path} className="an-row">
                  <span className="an-rank">{p.rank}</span>
                  <span className="an-row-main">
                    {p.id && p.kind ? (
                      <Link
                        className="an-row-title"
                        to={p.kind === 'article' ? `/content/${p.id}` : `/content?view=pages&page=${p.id}`}
                      >
                        {p.title || p.path}
                      </Link>
                    ) : (
                      <span className="an-row-title">{p.title || p.path}</span>
                    )}
                    {p.title && isHomePath(p.path) ? <HomeTag title={p.title} /> : null}
                    {p.title ? <span className="an-row-sub">{p.path}</span> : null}
                  </span>
                  <span className="an-row-n">
                    {p.clicks.toLocaleString()} · {p.impressions.toLocaleString()}
                    {p.position != null ? ` · #${p.position.toFixed(1)}` : ''}
                  </span>
                </div>
              ))}
            </div>
          ) : ready ? (
            <div className="an-empty-line">No page has had a click from Google in this range.</div>
          ) : (
            <GhostRows />
          )}
        </div>
      </div>
    </section>
  );
};
